import { atom, getDefaultStore } from 'jotai';
import { apiClient } from './apiClient';

export const TableFeatures = {
  BusinessActivity: 'BusinessActivity',
  DetailTypes: 'DetailTypes',
  TaskRelated: 'TaskRelated',
  AccountCategories: 'AccountCategories',
  AccountIds: 'AccountIds',
  FillingCategories: 'FillingCategories',
  States: 'States',
  AllClients: 'AllClients',
  Contacts: 'Contacts',
  Addresses: 'Addresses',
  OutsideProviders: 'OutsideProviders',
  BankAndCreditCards: 'BankAndCreditCards',
  EntityTypes: 'EntityTypes',
  SubParentStatus: 'SubParentStatus',
  EntityStates: 'EntityStates',
  Ownerships: 'Ownerships',
  IncomeSources: 'IncomeSources',
  Roles: 'Roles',
  Users: 'Users',
  ChangeBulk_EntityType: 'ChangeBulk_EntityType',
  ChangeBulk_SubParentStatus: 'ChangeBulk_SubParentStatus',
  ChangeBulk_EntityState: 'ChangeBulk_EntityState',
  Assignments: 'Assignments',
  TaskFilings: 'TaskFilings',
  AllTemplates: 'AllTemplates',
  DefaultStaff: 'DefaultStaff',
  TemplateStates: 'TemplateStates',
  TaxPayments: 'TaxPayments',
  SubEntityFilings: 'SubEntityFilings',
  TemplateRecurrenceSettings: 'TemplateRecurrenceSettings',
  ClientTemplates: 'ClientTemplates',
  ClientTemplateAssignments: 'ClientTemplateAssignments',
  HomePageTasks: 'HomePageTasks',
  TaskAssignments: 'TaskAssignments',
  TaskAuditHistory: 'TaskAuditHistory',
  GenerationLog: 'GenerationLog',
  FailedGeneratedTasks: 'FailedGeneratedTasks',
  SuccessfulGeneratedTasks: 'SuccessfulGeneratedTasks',
  ClientHistory: 'ClientHistory',
} as const;
export type TableFeature = (typeof TableFeatures)[keyof typeof TableFeatures];

export const tableFeaturesAtom = atom<Record<string, number>>({});
export const tablePreferencesAtom = atom<Record<number, string[]>>({});

export async function loadTableFeaturesAndPreferences() {
  const features = await apiClient.get<{ id: number; name: TableFeature }[]>(
    '/user-preference/table',
    {}
  );
  const store = getDefaultStore();
  store.set(
    tableFeaturesAtom,
    Object.fromEntries(features.map(({ name, id }) => [name, id]))
  );

  await Promise.all(
    features.map(({ id }) =>
      getTablePreference(id).then((preference) => {
        store.set(tablePreferencesAtom, (prev) => ({
          ...prev,
          [id]: preference.columnOrder,
        }));
      })
    )
  );
}

export function getTablePreference(featureId?: number) {
  if (featureId !== undefined) {
    const preferences = getDefaultStore().get(tablePreferencesAtom);
    if (preferences[featureId]?.length) {
      return Promise.resolve({ columnOrder: preferences[featureId] });
    } else {
      return apiClient.get<{ columnOrder: string[] }>(
        `/user-preference/table/${featureId}`,
        {}
      );
    }
  }

  return Promise.resolve({ columnOrder: [] });
}

export function saveTablePreference(payload: {
  sortColumn: string;
  sortOrder: string;
  columnOrder: string[];
  featureId?: number;
}) {
  if (payload.featureId !== undefined) {
    const store = getDefaultStore();
    store.set(tablePreferencesAtom, (prev) => ({
      ...prev,
      [payload.featureId!]: payload.columnOrder,
    }));
  }

  const response = apiClient.post<{ columnOrder: string[] }>(
    '/user-preference/table',
    payload
  );

  return response;
}

export function getTableFeatures() {
  const features = getDefaultStore().get(tableFeaturesAtom);
  if (Object.keys(features).length > 0) {
    const result = Object.entries(features).map(([name, id]) => ({
      id,
      name: name as TableFeature,
    }));
    return Promise.resolve(result);
  }

  return apiClient.get<{ id: number; name: TableFeature }[]>(
    '/user-preference/table',
    {}
  );
}
